import { emptySiteOnboardingDraft } from './onboarding-draft';
import {
  CANVAS_SESSION_STORAGE_PREFIX,
  type CanvasSessionRecord,
  type SessionArtifact,
} from './canvas-session-types';

const ACTIVE_KEY = `${CANVAS_SESSION_STORAGE_PREFIX}active`;

function now(): number {
  return Date.now();
}

export function createEmptySession(): CanvasSessionRecord {
  return {
    siteId: null,
    generationSessionId: null,
    draft: emptySiteOnboardingDraft(),
    messages: [],
    artifacts: [],
    phase: 'idle',
    lastError: null,
    editWorkingBuffer: null,
    publishedUrl: null,
    updatedAt: now(),
  };
}

function storageKeyForSite(siteId: string): string {
  return `${CANVAS_SESSION_STORAGE_PREFIX}${siteId}`;
}

export function loadPersistedSession(siteId: string | null): CanvasSessionRecord | null {
  if (typeof window === 'undefined') return null;
  const key = siteId ? storageKeyForSite(siteId) : ACTIVE_KEY;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CanvasSessionRecord;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function persistSession(record: CanvasSessionRecord): void {
  if (typeof window === 'undefined') return;
  const stamped: CanvasSessionRecord = { ...record, updatedAt: now() };
  window.localStorage.setItem(ACTIVE_KEY, JSON.stringify(stamped));
  if (stamped.siteId) {
    window.localStorage.setItem(storageKeyForSite(stamped.siteId), JSON.stringify(stamped));
  }
}

export function appendArtifact(
  record: CanvasSessionRecord,
  artifact: Omit<SessionArtifact, 'id' | 'createdAt'>,
): CanvasSessionRecord {
  const entry: SessionArtifact = {
    ...artifact,
    id: `${artifact.kind}-${now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: now(),
  };
  return {
    ...record,
    artifacts: [...record.artifacts, entry],
    updatedAt: now(),
  };
}

export function clearActiveSession(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ACTIVE_KEY);
}
